// LRU Cache implementation for API responses
class LRUCache {
    constructor(maxSize = 50, ttl = 5 * 60 * 1000) { // Default 5 minutes TTL
        this.maxSize = maxSize;
        this.ttl = ttl;
        this.cache = new Map();
    }

    // Generate cache key from query parameters
    static generateKey(params) {
        const sorted = Object.keys(params)
            .sort()
            .reduce((acc, key) => {
                if (params[key] !== undefined && params[key] !== null) {
                    acc[key] = params[key];
                }
                return acc;
            }, {});
        return JSON.stringify(sorted);
    }

    // Get value from cache
    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;

        // Check if expired
        if (Date.now() > item.expiry) {
            this.cache.delete(key);
            return null;
        }

        // Move to end (most recently used)
        this.cache.delete(key);
        this.cache.set(key, item);

        return item.value;
    }

    // Set value in cache
    set(key, value) {
        // Remove oldest item if at capacity
        if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
        }

        this.cache.set(key, {
            value,
            expiry: Date.now() + this.ttl
        });
    }

    // Clear cache
    clear() {
        this.cache.clear();
    }

    // Clear expired entries
    clearExpired() {
        const now = Date.now();
        for (const [key, item] of this.cache.entries()) {
            if (now > item.expiry) {
                this.cache.delete(key);
            }
        }
    }

    // Get cache size
    size() {
        return this.cache.size;
    }
}

// Request deduplication to prevent duplicate API calls
class RequestDeduplicator {
    constructor() {
        this.pending = new Map();
    }

    async deduplicate(key, requestFn) {
        // If request is already pending, return the same promise
        if (this.pending.has(key)) {
            return this.pending.get(key);
        }

        // Create new request promise
        const promise = requestFn()
            .finally(() => {
                // Remove from pending after completion
                this.pending.delete(key);
            });

        this.pending.set(key, promise);
        return promise;
    }

    clear() {
        this.pending.clear();
    }
}

// Create singleton instances
export const profileCache = new LRUCache(100, 5 * 60 * 1000); // 100 items, 5 min TTL
export const searchCache = new LRUCache(50, 3 * 60 * 1000); // 50 items, 3 min TTL
export const requestDeduplicator = new RequestDeduplicator();

// Export the classes for testing
export { LRUCache, RequestDeduplicator };